import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { TrendingUp, Target, Zap, BarChart3, Users, Rocket } from 'lucide-react'

export default function Home() {
  return (
    <>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-white via-cloud-50/30 to-white">
        <div className="container py-24 md:py-40">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="inline-block mb-6 px-5 py-2.5 bg-gradient-to-r from-carrot-50 to-carrot-100/50 text-carrot-700 rounded-full text-sm font-semibold tracking-wide shadow-subtle"
              >
                Smart Ads, Real Results
              </motion.div>
              <h1 className="text-6xl md:text-7xl font-bold leading-[1.1] text-ink-900 mb-8">
                Smarter campaigns.{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-carrot-500 to-carrot-600 relative inline-block">
                  Faster growth.
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-ink-600 leading-relaxed font-light mb-10">
                Performance marketing that compounds results across paid media,
                organic channels, and conversion optimization.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/pricing"
                  className="group inline-flex items-center justify-center px-8 py-4 bg-carrot-500 hover:bg-carrot-600 text-white font-semibold rounded-2xl shadow-soft hover:shadow-card-hover transition-all duration-300 ease-apple transform hover:scale-[1.02]"
                >
                  Book a Strategy Call
                  <Rocket className="ml-2 w-5 h-5 group-hover:translate-x-0.5 transition-transform duration-300" />
                </Link>
                <Link
                  to="/case-studies"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white hover:bg-ink-50 text-ink-800 font-semibold rounded-2xl border border-ink-200 hover:border-ink-300 transition-all duration-300 ease-apple shadow-subtle"
                >
                  See Case Studies
                </Link>
              </div>

              {/* Stats */}
              <div className="mt-16 grid grid-cols-3 gap-8">
                {[
                  { value: '+38%', label: 'Avg. ROAS' },
                  { value: '-27%', label: 'CPA' },
                  { value: '9.4/10', label: 'NPS' },
                ].map((stat, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 + idx * 0.1, ease: [0.16, 1, 0.3, 1] }}
                    className="text-center"
                  >
                    <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-br from-carrot-500 to-carrot-600">
                      {stat.value}
                    </div>
                    <div className="text-sm md:text-base text-ink-500 mt-2 font-medium">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="relative hidden md:block"
            >
              <div className="relative aspect-square p-20 flex items-center justify-center">
                <motion.img
                  src="/AdKarrot_white_color_transparent.png"
                  className="w-full h-full relative z-10"
                  alt="AdKarrot Smart Ads, Real Results"
                  animate={{ y: [0, -12, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                />
              </div>
            </motion.div>
          </div>
        </div>

      </section>

      {/* Services Overview */}
      <section className="py-32 bg-white">
        <div className="container">
          <div className="text-center max-w-2xl mx-auto mb-20">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="text-5xl md:text-6xl font-bold text-ink-900 mb-6 leading-tight"
            >
              Full-Stack Digital Marketing
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="text-xl md:text-2xl text-ink-600 font-light"
            >
              From strategy to execution, we handle every aspect of your digital growth.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Target,
                title: 'Paid Advertising',
                description: 'Google Ads, Facebook, LinkedIn campaigns optimized for ROI',
                color: 'carrot',
              },
              {
                icon: TrendingUp,
                title: 'SEO & Content',
                description: 'Organic growth through strategic content and technical SEO',
                color: 'leaf',
              },
              {
                icon: Zap,
                title: 'Conversion Optimization',
                description: 'Landing pages and funnels designed to convert',
                color: 'sun',
              },
              {
                icon: BarChart3,
                title: 'Analytics & Reporting',
                description: 'Data-driven insights with transparent, actionable reports',
                color: 'carrot',
              },
              {
                icon: Users,
                title: 'Social Media',
                description: 'Content creation and community management that engages',
                color: 'leaf',
              },
              {
                icon: Rocket,
                title: 'Growth Strategy',
                description: 'Comprehensive roadmaps tailored to your business goals',
                color: 'sun',
              },
            ].map((service, idx) => {
              const Icon = service.icon
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: idx * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  className="group bg-white rounded-3xl border border-ink-100 p-10 hover:border-carrot-200 hover:shadow-soft transition-all duration-500 ease-apple hover:-translate-y-1"
                >
                  <div className={`w-16 h-16 rounded-2xl bg-${service.color}-100 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 ease-apple shadow-subtle`}>
                    <Icon className={`w-8 h-8 text-${service.color}-600`} />
                  </div>
                  <h3 className="text-2xl font-bold text-ink-900 mb-4 tracking-tight">{service.title}</h3>
                  <p className="text-ink-600 leading-relaxed text-lg font-light">{service.description}</p>
                </motion.div>
              )
            })}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-center mt-16"
          >
            <Link
              to="/services"
              className="group inline-flex items-center text-carrot-600 hover:text-carrot-700 font-semibold text-xl"
            >
              Explore All Services
              <svg
                className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300 ease-apple"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900 text-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-carrot-500 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-carrot-600 rounded-full blur-3xl" />
        </div>

        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <h2 className="text-5xl md:text-6xl font-bold mb-8 leading-tight">
                Ready to grow your business?
              </h2>
              <p className="text-xl md:text-2xl text-cloud-200 mb-12 leading-relaxed font-light max-w-3xl mx-auto">
                Book a free 30-minute strategy call. We'll analyze your current marketing, identify
                opportunities, and create a custom growth roadmap—no obligations.
              </p>
              <Link
                to="/pricing"
                className="group inline-flex items-center px-10 py-5 bg-carrot-500 hover:bg-carrot-600 text-white font-semibold text-lg rounded-2xl shadow-glow hover:shadow-card-hover transition-all duration-300 ease-apple transform hover:scale-[1.02]"
              >
                Get Started Today
                <Rocket className="ml-3 w-6 h-6 group-hover:translate-x-0.5 transition-transform duration-300" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  )
}
